import Dexie, { type EntityTable } from 'dexie';
import { erzeugeGeraeteId } from '@uep/shared';
import type {
  ImportProfilLokal,
  LokaleEinheit,
  LokaleSignatur,
  LokalerRaum,
  LokalerTermin,
  LokalesDokument,
  LokalesFoto,
  LokalesObjekt,
  LokalesProtokoll,
  LokalesProfil,
  LokalesTemplate,
  MetaEintrag,
  OutboxEintrag,
} from './typen';

/**
 * Lokale Datenbank (PHASENPLAN 1.1).
 *
 * Alles, was die App anzeigt, kommt aus dieser Datenbank — auch online.
 * Der Sync füllt sie, die UI liest nur hier. Dadurch verhält sich die App
 * offline und online identisch (CLAUDE.md §3.4).
 */

export class UepDatenbank extends Dexie {
  protokolle!: EntityTable<LokalesProtokoll, 'id'>;
  objekte!: EntityTable<LokalesObjekt, 'id'>;
  einheiten!: EntityTable<LokaleEinheit, 'id'>;
  raeume!: EntityTable<LokalerRaum, 'id'>;
  templates!: EntityTable<LokalesTemplate, 'id'>;
  fotos!: EntityTable<LokalesFoto, 'id'>;
  dokumente!: EntityTable<LokalesDokument, 'id'>;
  signaturen!: EntityTable<LokaleSignatur, 'id'>;
  termine!: EntityTable<LokalerTermin, 'id'>;
  outbox!: EntityTable<OutboxEintrag, 'id'>;
  meta!: EntityTable<MetaEintrag, 'schluessel'>;
  importProfile!: EntityTable<ImportProfilLokal, 'id'>;
  profile!: EntityTable<LokalesProfil, 'id'>;

  constructor(name = 'uebergabeprotokoll') {
    super(name);

    this.version(1).stores({
      protokolle:
        'id, objektId, status, synchronisiert, konfliktkopie, geloescht, uebergabeDatum, ' +
        '[objektId+uebergabeDatum], [status+uebergabeDatum], protokollNr, vorprotokollId, lokalGeaendertAm',
      objekte: 'id, objektNr, suchtext, aktiv, synchronisiert, geloescht, bezeichnung',
      raeume: 'id, objektId, [objektId+reihenfolge], synchronisiert, geloescht',
      templates: 'id, templateId, aktiv, art, [templateId+version]',
      fotos: 'id, protokollId, objektId, hochgeladen, erstelltAm',
      dokumente:
        'id, objektId, protokollId, typ, [objektId+typ], synchronisiert, geloescht, erstelltAm',
      signaturen: 'id, protokollId, synchronisiert',
      termine:
        'id, objektId, protokollId, beginn, zugewiesenAn, erledigt, synchronisiert, geloescht',
      // Die id ist eine UUID v7 — die Primärschlüsselreihenfolge ist damit FIFO.
      outbox: 'id, status, gruppe, naechsterVersuchAm, [status+naechsterVersuchAm], entitaetId',
      meta: 'schluessel',
      importProfile: 'id, name, synchronisiert',
    });

    /**
     * Version 2 — Einheitenebene (Objekt → Einheit → Raum).
     *
     * Der Upgrade setzt nur `einheitId: null`, damit das Feld überall existiert.
     * Die Standardeinheiten legt `einheiten.ts` beim nächsten Sync an: Dort
     * gehören die passenden Outbox-Einträge dazu, und ein Dexie-Upgrade ist
     * nicht der Ort, an dem man Datensätze zum Senden vormerkt.
     */
    this.version(2)
      .stores({
        protokolle:
          'id, objektId, einheitId, status, synchronisiert, konfliktkopie, geloescht, uebergabeDatum, ' +
          '[objektId+uebergabeDatum], [einheitId+uebergabeDatum], [status+uebergabeDatum], ' +
          'protokollNr, vorprotokollId, lokalGeaendertAm',
        einheiten:
          'id, objektId, [objektId+reihenfolge], einheitNr, aktiv, synchronisiert, geloescht',
        raeume:
          'id, objektId, einheitId, [objektId+reihenfolge], [einheitId+reihenfolge], ' +
          'synchronisiert, geloescht',
      })
      .upgrade(async (transaktion) => {
        await transaktion
          .table('raeume')
          .toCollection()
          .modify((raum: { einheitId?: string | null }) => {
            raum.einheitId = raum.einheitId ?? null;
          });
        await transaktion
          .table('protokolle')
          .toCollection()
          .modify((protokoll: { einheitId?: string | null }) => {
            protokoll.einheitId = protokoll.einheitId ?? null;
          });
      });

    /**
     * Version 3 — persönliches Profil.
     *
     * Nur eine neue Tabelle, kein Upgrade nötig: Wer noch kein Profil hat,
     * bekommt beim nächsten Sync das vom Server, sonst bleibt es leer.
     */
    this.version(3).stores({ profile: 'id, synchronisiert' });
  }
}

export const db = new UepDatenbank();

// ---------------------------------------------------------------------------
// Meta-Werte
// ---------------------------------------------------------------------------

export const METAS = {
  geraeteId: 'geraeteId',
  letzterPull: 'letzterPull',
  letzterSync: 'letzterSync',
  farbmodus: 'farbmodus',
  angemeldetAls: 'angemeldetAls',
  /** Cognito-`sub` des angemeldeten Nutzers — Schlüssel des eigenen Profils. */
  nutzerSchluessel: 'nutzerSchluessel',
  speicherWarnungGezeigt: 'speicherWarnungGezeigt',
} as const;

export const metaLesen = async <T>(schluessel: string, standard: T): Promise<T> => {
  const eintrag = await db.meta.get(schluessel);
  return eintrag ? (eintrag.wert as T) : standard;
};

export const metaSchreiben = async (schluessel: string, wert: unknown): Promise<void> => {
  await db.meta.put({ schluessel, wert, geaendertAm: new Date().toISOString() });
};

/**
 * Stabile Geräte-ID. Sie geht in jede Signatur ein und darf sich deshalb nicht
 * bei jedem Start ändern — sie lebt in IndexedDB, nicht in `localStorage`.
 */
export const geraeteId = async (): Promise<string> => {
  const vorhanden = await metaLesen<string | null>(METAS.geraeteId, null);
  if (vorhanden) return vorhanden;
  const neu = erzeugeGeraeteId();
  await metaSchreiben(METAS.geraeteId, neu);
  return neu;
};

/**
 * Leert alle Tabellen. Wird beim Abmelden benutzt — auf einem geteilten Tablet
 * dürfen keine Protokolldaten des Vorgängers zurückbleiben.
 */
export const datenbankLeeren = async (behalteGeraeteId = true): Promise<void> => {
  const id = behalteGeraeteId ? await metaLesen<string | null>(METAS.geraeteId, null) : null;
  await db.transaction('rw', db.tables, async () => {
    await Promise.all(db.tables.map((tabelle) => tabelle.clear()));
  });
  if (id) await metaSchreiben(METAS.geraeteId, id);
};

/** Suchtext eines Objekts — einheitlich erzeugt, damit die Suche verlässlich trifft. */
export const objektSuchtext = (objekt: {
  objektNr: string;
  bezeichnung: string;
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  lage?: string | null;
  eigentuemer?: string | null;
}): string =>
  [
    objekt.objektNr,
    objekt.bezeichnung,
    objekt.strasse,
    objekt.plz,
    objekt.ort,
    objekt.lage,
    objekt.eigentuemer,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
